<template>
  <div class="google-registration-page">
    <section class="google-registration-card">
      <p class="eyebrow">GOOGLE-KIRJAUTUMINEN</p>
      <h1>Valitse remmille nimi</h1>
      <p class="intro">
        Olet kirjautunut Googlella. Valitse vielä julkinen remmin nimi ja hyväksy käyttöehdot,
        niin pääset kokoamaan joukkuettasi.
      </p>

      <form @submit.prevent="completeRegistration" novalidate>
        <label for="google-registration-username">Remmin nimi</label>
        <input
          id="google-registration-username"
          v-model="username"
          type="text"
          maxlength="50"
          autocomplete="nickname"
          placeholder="Esim. Rami-Remmikäs"
          :disabled="isSubmitting"
          @input="clearError"
        />
        <p class="field-note">Nimi näkyy muille pelaajille. 3-50 merkkiä, kirjaimia, numeroita, väliviivoja ja alaviivoja.</p>

        <label class="terms-label">
          <input v-model="termsAccepted" type="checkbox" :disabled="isSubmitting" />
          <span>
            Hyväksyn <router-link to="/terms-of-service" target="_blank">käyttöehdot</router-link>.
          </span>
        </label>

        <p v-if="error" class="error-message">{{ error }}</p>
        <button type="submit" :disabled="isSubmitting || !termsAccepted">
          {{ isSubmitting ? 'Tallennetaan...' : 'Jatka Raviremmiin' }}
        </button>
      </form>
    </section>
  </div>
</template>

<script>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import axios from '../../plugins/axios';

const USERNAME_PATTERN = /^[A-Za-zÄÖÅäöå0-9]+(?:[_-]?[A-Za-zÄÖÅäöå0-9]+)*$/;

export default {
  name: 'CompleteGoogleRegistration',
  setup() {
    const username = ref('');
    const termsAccepted = ref(false);
    const isSubmitting = ref(false);
    const error = ref('');
    const router = useRouter();

    const clearError = () => {
      error.value = '';
    };

    const validateUsername = () => {
      const candidate = username.value.trim();
      username.value = candidate;
      if (candidate.length < 3 || candidate.length > 50 || !USERNAME_PATTERN.test(candidate)) {
        return 'Tarkista remmin nimi. Sen tulee olla 3-50 merkkiä pitkä ja sisältää vain sallittuja merkkejä.';
      }
      return '';
    };

    const completeRegistration = async () => {
      error.value = validateUsername();
      if (error.value || !termsAccepted.value || isSubmitting.value) return;

      isSubmitting.value = true;
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/profile/complete-google-registration`,
          { username: username.value, termsAccepted: true },
        );
        const completedUsername = response.data?.username || username.value;
        window.dispatchEvent(new CustomEvent('raviremmi:profile-completed', {
          detail: { username: completedUsername },
        }));
        await router.replace('/team');
      } catch (requestError) {
        error.value = requestError?.response?.data?.message
          || 'Rekisteröinnin viimeistely epäonnistui. Yritä uudelleen.';
      } finally {
        isSubmitting.value = false;
      }
    };

    return {
      username,
      termsAccepted,
      isSubmitting,
      error,
      clearError,
      completeRegistration,
    };
  },
};
</script>

<style scoped>
.google-registration-page {
  display: grid;
  min-height: 42vh;
  place-items: center;
  padding: 24px;
}

.google-registration-card {
  width: min(100%, 520px);
  padding: clamp(26px, 6vw, 42px);
  background: #112240;
  border: 1px solid #1e3a5f;
  border-radius: 10px;
  box-shadow: 0 10px 30px -10px rgba(2, 12, 27, 0.7);
}

.eyebrow {
  margin: 0 0 10px;
  color: #64ffda;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
}

h1 {
  margin: 0;
  color: #e6f1ff;
  font-size: clamp(1.55rem, 5vw, 2rem);
}

.intro {
  margin: 14px 0 28px;
  color: #a8b2d1;
  line-height: 1.55;
}

form {
  display: grid;
  gap: 12px;
}

label {
  color: #e6f1ff;
  font-weight: 600;
}

input[type='text'] {
  min-width: 0;
  padding: 12px 14px;
  color: #e6f1ff;
  font-size: 1rem;
  background: #0a192f;
  border: 1px solid #1e3a5f;
  border-radius: 4px;
}

input[type='text']:focus {
  outline: 0;
  border-color: #64ffda;
  box-shadow: 0 0 0 3px rgba(100, 255, 218, 0.15);
}

.field-note {
  margin: -2px 0 10px;
  color: #8892b0;
  font-size: 0.8rem;
  line-height: 1.45;
}

.terms-label {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  color: #a8b2d1;
  font-size: 0.9rem;
  font-weight: 400;
  line-height: 1.45;
}

.terms-label input {
  margin-top: 3px;
}

.terms-label a {
  color: #64ffda;
}

button[type='submit'] {
  margin-top: 10px;
  padding: 12px 18px;
  color: #0a192f;
  font-weight: 700;
  cursor: pointer;
  background: #64ffda;
  border: 0;
  border-radius: 4px;
}

button[type='submit']:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.error-message {
  margin: 2px 0 0;
  padding: 10px 12px;
  color: #ffb4b4;
  font-size: 0.9rem;
  background: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.35);
  border-radius: 4px;
}
</style>
